import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Mail, Send, ArrowLeft, CheckCircle2 } from "lucide-react";

const CATEGORIES = [
  { value: "general",     label: "Question générale" },
  { value: "support",     label: "Support technique" },
  { value: "partnership", label: "Partenariat / Prop-firm" },
  { value: "press",       label: "Presse / Média" },
  { value: "bug",         label: "Signaler un bug" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", category: "general" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || form.message.length < 10) {
      toast.error("Merci de remplir tous les champs (message ≥ 10 caractères)");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const j = await res.json();
      if (!res.ok || !j.ok) throw new Error(j.detail || "Erreur envoi");
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Erreur envoi du message");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050a13] text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full panel p-8 text-center" data-testid="contact-success">
          <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Message reçu</h1>
          <p className="text-white/70 mb-6">
            Merci {form.name} ! Nous vous répondrons à <span className="font-mono text-emerald-300">{form.email}</span> sous 48h ouvrées.
          </p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2" data-testid="contact-back-home"><ArrowLeft size={16} /> Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050a13] text-white px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-white/50 hover:text-white text-sm inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} /> Accueil</Link>
        <div className="mb-8">
          <div className="text-accent-blue text-xs font-mono uppercase tracking-widest mb-2">
            <Mail size={12} className="inline mr-1" /> Contact
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">Nous contacter</h1>
          <p className="text-white/70 text-base">
            Questions, partenariats, presse — nous vous répondons sous 48h ouvrées.
            Pour un accès Beta, utilisez plutôt le formulaire <Link to="/beta" className="text-accent-blue hover:underline">Beta Application</Link>.
          </p>
        </div>

        <form onSubmit={submit} className="panel p-6 space-y-4" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 block mb-1">Nom complet *</label>
              <input value={form.name} onChange={(e) => upd("name", e.target.value)} className="field w-full" data-testid="contact-name" required />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 block mb-1">Email *</label>
              <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} className="field w-full" data-testid="contact-email" required />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 block mb-1">Catégorie *</label>
            <select value={form.category} onChange={(e) => upd("category", e.target.value)} className="field w-full" data-testid="contact-category">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 block mb-1">Sujet *</label>
            <input value={form.subject} onChange={(e) => upd("subject", e.target.value)} className="field w-full" data-testid="contact-subject" required />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 block mb-1">Message *</label>
            <textarea value={form.message} onChange={(e) => upd("message", e.target.value)} rows={6} className="field w-full resize-y" data-testid="contact-message" required minLength={10} />
            <div className="text-[10px] text-white/40 mt-1">{form.message.length}/5000 · minimum 10 caractères</div>
          </div>
          <button type="submit" disabled={submitting} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
            {submitting ? "Envoi…" : (<><Send size={16} /> Envoyer le message</>)}
          </button>
          <div className="text-[10px] text-white/40 text-center">
            En envoyant ce formulaire, vous acceptez notre <Link to="/privacy" className="underline">politique de confidentialité</Link>. Vos données sont stockées de manière sécurisée et non partagées.
          </div>
        </form>
      </div>
    </div>
  );
}
